import {
  CHECK_TYPE_KEYS,
  CHECK_TYPE_LABELS,
  type CheckTypeKey,
} from "../api/pricelist";
import type {
  CheckPackSnapshot,
  PriceListUsage,
  UserLimits,
  UserLimitsCounters,
  UserTariffPurchase,
  UserTariffRequest,
} from "../api/user";
import { getPurchaseDate, getPurchaseTitle } from "../api/user";
import { formatPricelistPrice, getPaymentPeriodLabel } from "./pricelistFormat";
import { formatTariffDate } from "./userTariffFormat";
import { isPeriodActive } from "./checksTopUp";

const LIMIT_TYPE_ORDER = ["site", "policy", "it"] as const;

const LIMIT_TYPE_LABELS: Record<(typeof LIMIT_TYPE_ORDER)[number], string> = {
  site: "Проверка сайтов",
  policy: "Проверка политики",
  it: "Проверка минцифры",
};

const CHECK_TYPE_TO_LIMIT_KEY: Record<CheckTypeKey, (typeof LIMIT_TYPE_ORDER)[number]> = {
  siteCheck: "site",
  policyCheck: "policy",
  it: "it",
};

export const getRequestedTariffBadge = (item: {
  paymentStatus?: string | null;
  status?: string | null;
}): string => {
  const status = (item.paymentStatus ?? item.status)?.trim().toLowerCase();
  if (status === "pending" || status === "awaiting_payment" || status === "waiting_payment") {
    return "Ожидает оплаты";
  }
  return "На рассмотрении";
};

export const isPendingPaymentStatus = (paymentStatus?: string | null): boolean => {
  const status = paymentStatus?.trim().toLowerCase();
  return status === "pending" || status === "awaiting_payment" || status === "waiting_payment";
};

export const isPaymentUrlExpired = (expiration?: string | null): boolean => {
  if (!expiration?.trim()) return false;
  const date = new Date(expiration);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() > date.getTime();
};

export const formatPaymentDateTime = (value?: string | null): string => {
  if (!value?.trim()) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatUserLimitsPeriodRange = (limits?: UserLimits | null): string => {
  if (!limits) return "—";
  const start = formatTariffDate(limits.periodStart);
  const end = formatTariffDate(limits.periodEnd);
  if (start === "—" && end === "—") return "—";
  return `${start} — ${end}`;
};

export const getUserLimitsStatusBadge = (limits?: UserLimits | null): string => {
  if (!limits) return "Нет периода";
  return isPeriodActive(limits) ? "Активен" : "Истёк";
};

export interface UserLimitsUsageRow {
  key: string;
  label: string;
  value: string;
  remaining: number;
  limit: number;
  used: number;
}

export const getUserLimitsUsageRows = (limits?: UserLimits | null): UserLimitsUsageRow[] => {
  if (!limits) return [];

  return LIMIT_TYPE_ORDER.flatMap((key) => {
    const limit = Number(limits.limits?.[key] ?? 0);
    const used = Number(limits.usage?.[key] ?? 0);
    const apiRemaining = Number(limits.remaining?.[key]);
    if (!Number.isFinite(limit) && !Number.isFinite(used) && !Number.isFinite(apiRemaining)) {
      return [];
    }
    if (limit <= 0 && used <= 0 && !(Number.isFinite(apiRemaining) && apiRemaining > 0)) {
      return [];
    }

    const safeLimit = Number.isFinite(limit) ? Math.max(0, limit) : 0;
    const safeUsed = Number.isFinite(used) ? Math.max(0, used) : 0;
    const remaining = Number.isFinite(apiRemaining)
      ? Math.max(0, apiRemaining)
      : Math.max(0, safeLimit - safeUsed);

    return [
      {
        key,
        label: LIMIT_TYPE_LABELS[key],
        value:
          safeLimit > 0
            ? `${remaining} из ${safeLimit}`
            : `${safeUsed} использовано`,
        remaining,
        limit: safeLimit,
        used: safeUsed,
      },
    ];
  });
};

export interface UserLimitsEntitlementRow {
  key: CheckTypeKey;
  label: string;
  options: string[];
  categoryCount: number;
}

export const getUserLimitsEntitlementRows = (
  limits?: UserLimits | null
): UserLimitsEntitlementRow[] => {
  if (!limits?.entitlements) return [];

  return CHECK_TYPE_KEYS.flatMap((key) => {
    const config = limits.entitlements?.[key];
    if (!config?.enabled) return [];
    const options: string[] = [];
    if (config.options?.schedule) options.push("расписание");
    if (config.options?.report) options.push("отчёт на почту");
    return [
      {
        key,
        label: CHECK_TYPE_LABELS[key],
        options,
        categoryCount: config.categoryIds?.length ?? 0,
      },
    ];
  });
};

export const formatLimitsDelta = (delta?: UserLimitsCounters | null): string => {
  if (!delta) return "—";
  const parts = LIMIT_TYPE_ORDER.flatMap((key) => {
    const value = Number(delta[key] ?? 0);
    if (!Number.isFinite(value) || value === 0) return [];
    return [`${LIMIT_TYPE_LABELS[key]}: +${value}`];
  });
  return parts.length ? parts.join(", ") : "—";
};

export const formatCheckPacksSummary = (packs?: CheckPackSnapshot[] | null): string => {
  if (!packs?.length) return "—";
  return packs
    .map((pack) => {
      const label =
        pack.checkType && CHECK_TYPE_LABELS[pack.checkType]
          ? CHECK_TYPE_LABELS[pack.checkType]
          : pack.checkType ?? "Проверки";
      const count = pack.count ?? 0;
      return `${label}: ${count}`;
    })
    .join(", ");
};

export const getPurchaseTypeLabel = (purchase: UserTariffPurchase): string =>
  purchase.tariffType === "checks" ? "Докупка" : "Тариф";

export const formatPurchaseTitle = (purchase: UserTariffPurchase): string => {
  if (purchase.tariffType === "checks") {
    const packs = formatCheckPacksSummary(purchase.checkPacks);
    return packs !== "—" ? packs : "Докупка проверок";
  }
  return getPurchaseTitle(purchase) || "Тариф";
};

export const formatPurchasePrice = (purchase: UserTariffPurchase): string => {
  if (purchase.price == null || !Number.isFinite(purchase.price)) return "—";
  const price = formatPricelistPrice(purchase.price);
  if (purchase.tariffType === "checks" || !purchase.period) return price;
  return `${price} / ${getPaymentPeriodLabel(purchase.period).toLowerCase()}`;
};

export const formatPurchaseDate = (purchase: UserTariffPurchase): string =>
  formatTariffDate(getPurchaseDate(purchase));

const PURCHASE_STATUS_LABELS: Record<string, string> = {
  requested: "Запрошен",
  active: "Активен",
  archived: "В архиве",
};

export const formatPurchaseStatus = (status?: string | null): string => {
  const key = status?.trim().toLowerCase();
  if (!key) return "—";
  return PURCHASE_STATUS_LABELS[key] ?? status!.trim();
};

export const formatRequestedPurchaseTitle = (
  request: Pick<PriceListUsage | UserTariffRequest, "tariffType" | "tariffName" | "checkPacks">
): string => {
  if (request.tariffType === "checks") {
    const packs = formatCheckPacksSummary(request.checkPacks);
    return packs !== "—" ? packs : "Докупка проверок";
  }
  return request.tariffName?.trim() || "Заявка на тариф";
};

export const getLimitKeyForCheckType = (key: CheckTypeKey) => CHECK_TYPE_TO_LIMIT_KEY[key];
